import { useEffect, useRef, useState } from "react";
import { hasConsent } from "@/lib/cookie-consent";
import { useLocation } from "@tanstack/react-router";
import { captureAttribution, getAttribution } from "@/lib/attribution";
import { getSessionId } from "@/lib/session-id";

/**
 * Anonymous visitor tracker. Records one event per route navigation
 * (path + referrer + session id + UTM attribution). Skips admin
 * traffic.
 *
 * Uses the SAME session id as `track()`, so page views and funnel events
 * can be joined into one visit.
 */
export function VisitorTracker() {
  const { pathname } = useLocation();
  const lastSent = useRef<string | null>(null);
  // Page views are non-essential analytics: only record them once the
  // visitor has said yes to analytics in the cookie banner.
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const sync = () => setAllowed(hasConsent("analytics"));
    sync();
    window.addEventListener("rt-consent-change", sync);
    return () => window.removeEventListener("rt-consent-change", sync);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!allowed) return;
    if (pathname.startsWith("/admin") || pathname === "/admin-login") return;
    if (lastSent.current === pathname) return;
    lastSent.current = pathname;

    try {
      const sid = getSessionId();
      if (!sid) return;


      // Capture or upgrade attribution from the current URL.
      captureAttribution();
      const attr = getAttribution();

      void fetch("/api/public/pageview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sid,
          path: pathname,
          referrer: document.referrer || null,
          utm_source: attr?.utm_source ?? null,
          utm_medium: attr?.utm_medium ?? null,
          utm_campaign: attr?.utm_campaign ?? null,
          utm_content: attr?.utm_content ?? null,
          utm_term: attr?.utm_term ?? null,
          platform: attr?.platform ?? null,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignore */
    }
  }, [pathname, allowed]);

  return null;
}
